import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Row, Col } from 'react-flexbox-grid';
import config from '../../config';
import TableRankingIndividualItem from '../TableRankingIndividualItem';
import EmptyState from '../EmptyState';
import { handleResponse, serializeQuery } from '../../utils/handleResponse';

const TableRankingIndividual = ({
  raceDay,
  raceEvent,
  setShowLoading,
  setShowSecondLoading,
  individualFavorites,
  saveFavorite,
  deleteFavorite,
  selectedGenderFilter,
  selectedIndividualCategoryFilter,
  searchText,
  changeIndividual,
  favoritesEnabled,
}) => {
  const [rakings, setRakings] = useState([]);
  const [rakingsFavorite, setRakingsFavorite] = useState([]);
  const isPreviewMode =
    new URLSearchParams(window.location.search).get('preview') === '1';
  const hasMeaningfulTimes = rakings.some(
    (raking) => Number(raking?.elapsed_time || 0) > 0
  );
  const shouldShowRankings = hasMeaningfulTimes || isPreviewMode;
  const normalizeGender = (value) => {
    const gender = String(value || '')
      .trim()
      .toUpperCase();
    if (['M', 'MALE', 'MEN', 'HOMBRE'].includes(gender)) return 'male';
    if (['F', 'FEMALE', 'WOMEN', 'MUJER'].includes(gender)) return 'female';
    return null;
  };

  const applyIndividualFilters = (participants) => {
    const filteredByGender = selectedGenderFilter
      ? participants.filter(
          (participant) =>
            normalizeGender(participant?.gender) === selectedGenderFilter
        )
      : participants;

    if (selectedIndividualCategoryFilter !== 'PRO') return filteredByGender;

    return filteredByGender.filter((participant) =>
      String(participant?.category || '')
        .toUpperCase()
        .includes('PRO')
    );
  };

  const applyProFilter = (participants) => {
    return applyIndividualFilters(participants);
  };

  const normalizeSearch = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const applySearchFilter = (participants) => {
    const term = normalizeSearch(searchText).trim();
    if (!term) return participants;
    return participants.filter((participant) => {
      const fullName = normalizeSearch(
        `${participant?.name || ''} ${participant?.last_name || ''}`
      );
      const teamName = normalizeSearch(participant?.team?.name);
      return fullName.includes(term) || teamName.includes(term);
    });
  };

  const getRanking = async () => {
    setShowLoading(true);
    let athlete = null;
    try {
      athlete = JSON.parse(localStorage.getItem('userData') || 'null');
    } catch (e) {
      athlete = null;
    }
    const headers = athlete?.userToken
      ? {
          Authorization: `Bearer ${athlete.userToken}`,
        }
      : undefined;

    const requestOptions = {
      method: 'GET',
      ...(headers ? { headers } : {}),
    };

    const params = {
      page: 1,
      limit: 2000,
      orderByTotalDistance: true,
      includeTeams: true,
      ignoreTotalDistance: true,
    };

    if (selectedGenderFilter) {
      params['gender'] = selectedGenderFilter;
    }

    // if (filterText) {
    //   params['search'] = filterText;
    // }

    const url = new URL(
      `${config.tncApiUrl}/sport-events/individual-participants/${config.eventId}`
    );
    url.search = serializeQuery(params);

    const response = await fetch(url, requestOptions);

    setShowLoading(false);
    try {
      const newRankings = await handleResponse(response);

      const participants = newRankings.data.participants.map((participant) => {
          if (participant.team_id) {
            const team = newRankings.data.teams.find(
              (t) => t.id === participant.team_id
            );
            return {
              ...participant,
              team: team || null,
            };
          }
          return participant;
        });
      setRakings(applyProFilter(participants));
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (!rakings.length || !individualFavorites.length) {
      setRakingsFavorite([]);
      return;
    }
    // IDs (userId) que el usuario marcó como favoritos
    const favIdSet = new Set(individualFavorites.map((f) => f.favoriteId));

    const favTimes = rakings.filter((st) => favIdSet.has(st.user_id));
    console.log('favTimes', favTimes);
    setRakingsFavorite(favTimes);
  }, [rakings, individualFavorites]);

  useEffect(() => {
    if (raceDay) {
      getRanking();
    }
  }, [raceDay]);

  useEffect(() => {
    if (changeIndividual) {
      getRanking();
    }
  }, [changeIndividual]);

  const visibleRankings = applySearchFilter(rakings);
  const visibleRankingsFavorite = applySearchFilter(rakingsFavorite);

  return (
    <Col xs={12}>
      <Row>
        {raceDay &&
          shouldShowRankings &&
          visibleRankings.length > 0 &&
          visibleRankingsFavorite.map((raking) => {
            return (
              <TableRankingIndividualItem
                userInfo={raking}
                key={raking._id}
                darkMode
                firstRaking={visibleRankings[0]}
                individualFavorites={individualFavorites}
                saveFavorite={saveFavorite}
                deleteFavorite={deleteFavorite}
                favoritesEnabled={favoritesEnabled}
              />
            );
          })}
        {(!raceDay || !shouldShowRankings || visibleRankings.length === 0) && (
          <EmptyState />
        )}
        {raceDay &&
          shouldShowRankings &&
          visibleRankings.map((raking) => {
            return (
              <TableRankingIndividualItem
                userInfo={raking}
                key={raking._id}
                raceDay={raceDay}
                individualFavorites={individualFavorites}
                saveFavorite={saveFavorite}
                deleteFavorite={deleteFavorite}
                firstRaking={visibleRankings[0]}
                favoritesEnabled={favoritesEnabled}
              />
            );
          })}
      </Row>
    </Col>
  );
};

TableRankingIndividual.propTypes = {
  setShowLoading: PropTypes.func.isRequired,
  setShowSecondLoading: PropTypes.func.isRequired,
  raceDay: PropTypes.object.isRequired,
  raceEvent: PropTypes.object.isRequired,
  individualFavorites: PropTypes.array.isRequired,
  saveFavorite: PropTypes.func.isRequired,
  deleteFavorite: PropTypes.func.isRequired,
  selectedGenderFilter: PropTypes.string,
  selectedIndividualCategoryFilter: PropTypes.string,
  searchText: PropTypes.string,
  changeIndividual: PropTypes.number,
  favoritesEnabled: PropTypes.bool,
};

TableRankingIndividual.defaultProps = {
  selectedGenderFilter: null,
  selectedIndividualCategoryFilter: null,
  searchText: '',
  changeIndividual: null,
  favoritesEnabled: false,
};

export default TableRankingIndividual;
